import type { CSSProperties } from 'react'

import { cn } from '@/shared/lib/cn'
import { hueToCss, normalizeHue } from '@/shared/lib/company-hue'
import { initials } from '@/shared/lib/format'

export interface CompanyMonogramProps {
  name: string
  hue: number
  size?: 'sm' | 'md'
  className?: string
}

/**
 * Monograma de 2 letras en mono. Acompana a la espina en listas mezcladas y en
 * resultados de busqueda, donde una barra de 3px no alcanza para identificar la
 * empresa de un vistazo.
 */
export function CompanyMonogram({ name, hue, size = 'sm', className }: CompanyMonogramProps) {
  /**
   * El HUE lo pone la empresa; la LUMINOSIDAD la pone el tema.
   *
   * Estaba fija en 62% para los dos temas: sobre el fondo oscuro se lee, sobre
   * papel queda lavada. El QA funcional lo midió en 2.03:1 y lo llamó ilegible
   * (7.1); volviendo a medir sobre los cuatro hues del seed, en modo claro las
   * cuatro empresas daban entre 1.32:1 y 2.58:1 — ninguna llegaba a AA, y son
   * dos letras de 11px.
   *
   * Por eso la luminosidad sale de un token que está definido dos veces, uno
   * por tema (`--company-monogram-l` en `tokens.css`), que es la regla de la
   * dirección de diseño: el modo claro no es un filtro invertido del oscuro.
   */
  const h = normalizeHue(hue)
  const style: CSSProperties = {
    color: `hsl(${h} var(--company-monogram-s) var(--company-monogram-l))`,
    backgroundColor: hueToCss(hue, { saturation: 40, lightness: 50, alpha: 0.14 }),
    borderColor: hueToCss(hue, { saturation: 40, lightness: 50, alpha: 0.35 }),
  }

  return (
    <span
      style={style}
      title={name}
      className={cn(
        'num inline-flex shrink-0 items-center justify-center border font-medium tracking-tight',
        'rounded-[var(--radius-control)] uppercase',
        size === 'sm' ? 'h-5 min-w-5 px-1 text-2xs' : 'h-7 min-w-7 px-1.5 text-xs',
        className,
      )}
    >
      {initials(name)}
    </span>
  )
}
